/**
 * Hedita Enterprise - Team React Query Hooks
 * Nuovo schema: team_members con admin_id
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import type { TeamMember, TeamMemberInsert, TeamMemberUpdate } from './types';

const QUERY_KEY = 'team-members';

// Query keys
export const teamMembersKeys = {
  all: [QUERY_KEY] as const,
  lists: () => [...teamMembersKeys.all, 'list'] as const,
  list: (adminId: string) => [...teamMembersKeys.lists(), adminId] as const,
  details: () => [...teamMembersKeys.all, 'detail'] as const,
  detail: (id: string) => [...teamMembersKeys.details(), id] as const,
  stats: () => [...teamMembersKeys.all, 'stats'] as const,
};

// ============================================================
// QUERIES
// ============================================================

export function useTeamMembers(adminId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, adminId],
    queryFn: async () => {
      if (!adminId) return [];
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('admin_id', adminId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as TeamMember[];
    },
    enabled: !!adminId,
  });
}

export function useTeamMember(id?: string, adminId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, id, adminId],
    queryFn: async () => {
      if (!id || !adminId) return null;
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('id', id)
        .eq('admin_id', adminId)
        .single();
      if (error) throw error;
      return data as TeamMember | null;
    },
    enabled: !!id && !!adminId,
  });
}

// ============================================================
// MUTATIONS
// ============================================================

export function useCreateTeamMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ member, adminId }: { member: TeamMemberInsert; adminId: string }) => {
      const { data, error } = await supabase
        .from('team_members')
        .insert({ ...member, admin_id: adminId })
        .select()
        .single();
      if (error) throw error;
      return data as TeamMember;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.adminId] });
    },
  });
}

export function useUpdateTeamMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data, adminId }: { id: string; data: TeamMemberUpdate; adminId: string }) => {
      const { data: result, error } = await supabase
        .from('team_members')
        .update(data)
        .eq('id', id)
        .eq('admin_id', adminId)
        .select()
        .single();
      if (error) throw error;
      return result as TeamMember;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.adminId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id, variables.adminId] });
    },
  });
}

export function useDeleteTeamMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, adminId }: { id: string; adminId: string }) => {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', id)
        .eq('admin_id', adminId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.adminId] });
    },
  });
}

// ============================================================
// RETRO-COMPATIBILITÀ (deprecated)
// ============================================================

/** @deprecated Usa useTeamMembers */
export const useCollaboratori = useTeamMembers;
/** @deprecated Usa useTeamMember */
export const useCollaboratore = useTeamMember;
/** @deprecated Usa useCreateTeamMember */
export const useCreateCollaboratore = useCreateTeamMember;
/** @deprecated Usa useUpdateTeamMember */
export const useUpdateCollaboratore = useUpdateTeamMember;
/** @deprecated Usa useDeleteTeamMember */
export const useDeleteCollaboratore = useDeleteTeamMember;

/**
 * Function to fetch team members statistics
 */
async function getTeamMembersStats(adminId?: string) {
  if (!adminId) return null;
  
  const { data, error } = await supabase
    .from('team_members')
    .select('ruolo, attivo')
    .eq('admin_id', adminId);
    
  if (error) throw error;
  
  const members = data || [];
  return {
    totali: members.length,
    attivi: members.filter(m => m.attivo).length,
    admin: members.filter(m => m.ruolo === 'admin').length,
    manager: members.filter(m => m.ruolo === 'manager').length,
  };
}

/**
 * Hook to fetch team members statistics
 */
export function useTeamMembersStats() {
  const { currentUser } = useAuth();
  
  return useQuery({
    queryKey: teamMembersKeys.stats(),
    queryFn: () => getTeamMembersStats(currentUser?.id),
    enabled: !!currentUser?.id,
  });
}

/** @deprecated Usa useTeamMembersStats */
export const useCollaboratoriStats = useTeamMembersStats;
